// @ts-check
import { gql } from '@apollo/client/core';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Image, View } from '@react-pdf/renderer';
import React from 'react';
import {
  Box,
  Company,
  Flex,
  Image as BottlebooksImage,
  Page,
  Product,
  QRCode,
  // Stencil,
  Text,
  Title,
} from '../../pdf-design-system';
import {
  PagesProvider,
  usePage,
} from '../../pdf-design-system/Base/PageLayout/Page';
import sourceSansPro400 from '../../pdf-design-system/fonts/source-sans-pro/400';
import sourceSansPro500 from '../../pdf-design-system/fonts/source-sans-pro/500';
import UserLocaleProvider from '../../Providers/UserLocaleProvider';
import messages from './locale/messages';

const productCountFirstPage = 3;
const productCountOtherPages = 8;

export default function BottlebooksStandardCatalog({
  data,
  locale,
  options = { bleed: 0 },
  ...rest
}) {
  const { withBleed } = useBleed(options);
  const sections = afterQuery(data, rest);
  return (
    <PagesProvider>
      <UserLocaleProvider locale={locale} messages={messages}>
        {sections.map((section, index) => {
          const { registration } = section;
          const firstPageProducts =
            registration?.registeredProducts?.nodes?.slice(
              0,
              productCountFirstPage
            );
          const otherProducts = registration?.registeredProducts?.nodes?.slice(
            productCountFirstPage
          );

          const otherPages = Math.ceil(
            otherProducts?.length / productCountOtherPages
          );
          return (
            <React.Fragment key={`wrapper-${index}`}>
              <CatalogPage
                key={`first-${index}`}
                registration={registration}
                theme={theme}
                bleed={options.bleed}
                {...rest}
              >
                <PageHeader
                  theme={theme}
                  registration={registration}
                  style={{
                    position: 'absolute',
                    top: withBleed(7.5), // We need to add bleed here because of position: 'absolute'
                    left: 0,
                    right: 0,
                  }}
                  bleed={options.bleed}
                />
                <PageBody
                  style={{
                    marginTop: '69mm',
                  }}
                >
                  <View style={{ height: 319 }}>
                    <ExhibitorHeader registration={registration} />
                    <View style={{ height: '63mm', display: 'flex' }}>
                      {firstPageProducts?.map((registeredProduct, index) => (
                        <ProductRow
                          key={registeredProduct.productId}
                          registeredProduct={registeredProduct}
                          index={index + 1}
                          locale={locale}
                        />
                      ))}
                    </View>
                  </View>
                </PageBody>
              </CatalogPage>
              {Array.from(Array(otherPages)).map((_, pageIndex) => (
                <CatalogPage
                  key={`another-${pageIndex}`}
                  registration={registration}
                  theme={theme}
                  bleed={options.bleed}
                  {...rest}
                >
                  <StandNumber
                    registration={registration}
                    bleed={options.bleed}
                  />
                  <PageBody
                    style={{
                      marginTop: '12.5mm',
                      paddingBottom: '6.5mm',
                    }}
                  >
                    <View
                      style={{
                        height: '162.5mm',
                        display: 'flex',
                        borderTop: `1px solid #CCC`,
                      }}
                    >
                      {otherProducts
                        .slice(
                          pageIndex * productCountOtherPages,
                          pageIndex * productCountOtherPages +
                            productCountOtherPages
                        )
                        ?.map((registeredProduct, index) => (
                          <ProductRow
                            key={registeredProduct.productId}
                            registeredProduct={registeredProduct}
                            index={
                              pageIndex * productCountOtherPages +
                              productCountFirstPage +
                              index +
                              1
                            }
                            locale={locale}
                          />
                        ))}
                    </View>
                  </PageBody>
                </CatalogPage>
              ))}
            </React.Fragment>
          );
        })}
      </UserLocaleProvider>
    </PagesProvider>
  );
}

function StandNumber({ registration, bleed }) {
  const { withBleed } = useBleed({ bleed });
  const { alignment } = usePage();
  return (
    <View
      style={{
        position: 'absolute',
        top: withBleed(7.5),
        left: withBleed(15),
        right: withBleed(15),
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: alignment === 'left' ? 'row' : 'row-reverse',
      }}
    >
      <Text>{registration.stand?.name}</Text>
      <Text>{registration.profile?.name}</Text>
    </View>
  );
}

function useBleed({ bleed = 15 } = { bleed: 15 }) {
  /**
   * @param {number} measurementInMm
   * @returns
   */
  function withBleed(measurementInMm, factor = 1) {
    return `${measurementInMm + bleed * factor}mm`;
  }
  return { withBleed };
}

function CatalogPage({
  registration,
  theme,
  bleed = 15, // Rediculously high number so that we recognize when it isn't set correctly
  style = {},
  children,
  ...rest
}) {
  const { withBleed } = useBleed({ bleed });

  return (
    <Page
      size={{ width: withBleed(148, 2), height: withBleed(210, 2) }}
      style={[
        { paddingVertical: withBleed(0), paddingHorizontal: withBleed(0) },
        style,
      ]}
      theme={theme}
      {...rest}
    >
      {/* <Stencil
        src="https://res.cloudinary.com/bottlebooks/image/upload/v1658406260/pdf-objects/Bottlebooks/standard-catalog-stencil.png"
        style={{ height: '210mm', padding: `${bleed}mm` }}
      /> */}

      {children}

      <PageFooter registration={registration} />
    </Page>
  );
}

function PageFooter({ registration, ...rest }) {
  const { alignment } = usePage();
  return (
    <View style={{ display: 'flex', paddingHorizontal: '15mm' }} {...rest}>
      <View
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          flexDirection: 'row',
          height: 40,
        }}
      >
        {registration.collection?.site?.publicUrl &&
          registration.collection?.site?.publicUrl?.trim() !== '' && (
            <React.Fragment>
              <QRCode
                url={`${registration.collection?.site?.publicUrl}/e/${registration.companyId}`}
                errorCorrectionLevel="L"
                margin={2}
                style={{
                  width: '15mm',
                  height: '15mm',

                  // ...(alignment === 'left'
                  //   ? { right: withBleed(18) }
                  //   : { left: withBleed(18) }),
                }}
              />
              <View
                style={{
                  marginLeft: '1mm',
                  marginRight: '2mm',
                  paddingRight: '2mm',
                  borderRight: '1px solid #ccc',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Text variant="small" style={{ width: 45 }}>
                  Scan to make notes
                </Text>
              </View>
            </React.Fragment>
          )}
        <View style={{ flexGrow: 1 }}>
          <Text variant="small">{registration.profile?.website}</Text>
          <Text variant="small">{registration.profile?.email}</Text>
          <Text variant="small">{registration.profile?.telephone}</Text>
        </View>
      </View>
      <View
        style={{
          borderTop: '1px solid #CCC',
          marginTop: 8,
          paddingTop: 3,
          display: 'flex',
          flexDirection: alignment === 'right' ? 'row' : 'row-reverse',
          justifyContent: 'flex-end',
        }}
      >
        <Text variant="small" style={{ marginRight: 5 }}>
          {registration.collection.name}
        </Text>
        <BottlebooksImage
          src={registration.collection?.logo?.url}
          height={15}
          width={70}
          trim
        />
      </View>
    </View>
  );
}

function ProductRow({ registeredProduct, index, locale }) {
  const { i18n } = useLingui();
  const { alcohol, residualSugar, totalAcidity } = registeredProduct.product;
  const analytics = [
    alcohol && i18n._(t`Alc.: ${formatNumber({ locale, number: alcohol })}%`),
    residualSugar &&
      i18n._(t`RS: ${formatNumber({ locale, number: residualSugar })} g/l`),
    totalAcidity &&
      i18n._(t`TA: ${formatNumber({ locale, number: totalAcidity })} g/l`),
  ]
    .filter(Boolean)
    .join(' | ');
  return (
    <View
      style={{
        display: 'flex',
        flexDirection: 'row',
        height: '20mm',
        flexShrink: 0,
        paddingTop: '3mm',
        justifyContent: 'space-between',
      }}
    >
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          paddingRight: '2mm',
          width: 300,
        }}
      >
        <View style={{ width: '6mm' }}>
          <Text style={{ fontSize: 10.5 }}>{index}.</Text>
        </View>
        <View>
          <Text style={{ fontSize: 10.5 }}>
            {registeredProduct.product.name}
          </Text>
          <Text variant="light">{analytics}</Text>
          <Text variant="light">
            {grapeVarietyString(registeredProduct.product)}
          </Text>
        </View>
      </View>
      <View
        style={{
          width: 30,
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'row',
        }}
      >
        <Product.BottleImage product={registeredProduct.product} height={45} />
      </View>
    </View>
  );
}

function grapeVarietyString({ grapeVarieties }) {
  return grapeVarieties
    ?.map(({ percentage, varietyName }) => {
      return percentage ? `${percentage}% ${varietyName}` : `${varietyName}`;
    })
    .join(', ');
}

function PageHeader({ bleed, theme, registration, style = {} }) {
  const { withBleed } = useBleed({ bleed });
  return (
    <View
      style={[
        {
          backgroundColor: theme.colors.primary,
          paddingRight: withBleed(15),
          paddingLeft: withBleed(15),
          position: 'relative',
        },
        style,
      ]}
    >
      {registration.profile?.mainImage?.fixed?.src && (
        <Image
          src={registration.profile?.mainImage?.fixed?.src}
          // Ensure images with transparent background render are visible
          style={{ backgroundColor: 'white' }}
        />
      )}
    </View>
  );
}

function PageBody({ children, style, ...rest }) {
  return (
    <View
      style={[
        {
          paddingHorizontal: '15mm',
        },
        style,
      ]}
      {...rest}
    >
      {children}
    </View>
  );
}

/**
 * @param {object} props
 * @param {number} props.number
 * @param {string} props.number
 */
function formatNumber({ locale = 'en', number }) {
  if (number == null) return '';
  return locale === 'en'
    ? number.toFixed(1).replace(',', '.')
    : number.toFixed(1).replace('.', ',');
}

function ExhibitorHeader({ registration, ...rest }) {
  return (
    <View {...rest}>
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          marginBottom: 3,
          marginLeft: '3mm',
        }}
      >
        <View
          style={{
            backgroundColor: 'white',
            borderRadius: 3,
            border: '1px solid #ccc',
            padding: 5,
            marginTop: -35,
            marginRight: '3mm',
            display: 'flex',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Company.ProfileLogo
            companyProfile={{
              logo: registration.profile.autoSizedLogo,
            }}
            height={55}
            width={70}
          />
        </View>
        <View style={{ display: 'flex' }}>
          <Text variant="light">{registration.stand?.name || ' '}</Text>
          <Title style={{ width: 292, paddingRight: '2cm', height: 21 }}>
            {registration.profile.name}
          </Title>
          <Text variant="light">
            {registration.registeredProducts?.nodes?.[0]?.product
              .denomination || ' '}
          </Text>
        </View>
      </View>
      <View
        style={{
          borderBottom: '1px solid #CCC',
          paddingVertical: 5,
          maxHeight: 92,
        }}
      >
        <Text variant="light" style={{ fontSize: 9 }}>
          {registration.profile.shortDescription}
        </Text>
      </View>
    </View>
  );
}

/**
 * @typedef {import('../../pdf-design-system/useTheme').BottlebooksPDFTheme["theme"] & {
 *  colors?: Record<string, string>;
 * }} Theme
 */

const colors = {
  primary: '#a4b86c',
  accent: '#a4b86c',
};

/** @type {Theme} */
export const theme = {
  colors,
  components: {
    Text: {
      base: { ...sourceSansPro400, fontSize: '10pt' },
      small: { ...sourceSansPro400, fontSize: '9pt' },
      bold: { ...sourceSansPro500 },
      light: { color: '#555' },
    },
    Title: {
      base: { ...sourceSansPro400 },
      small: {
        fontSize: 8,
        lineHeight: 1,
      },
      default: {
        fontSize: '16pt',
      },
      large: {
        fontSize: 18,
        lineHeight: 1.3,
      },
    },
  },
};

BottlebooksStandardCatalog.fragment = gql`
  fragment BottlebooksStandardCatalog on CatalogConfiguration {
    catalogConfigurationId
    collectionId
    collection {
      collectionId
      eventId: collectionId
      site {
        publicUrl
      }
    }
    sections(filter: { registration: { companyId: { in: $companyIds } } }) {
      __typename
      ... on RegistrationCatalogSection {
        registration {
          registrationId
          companyId
        }
      }
      ... on UploadedFileCatalogSection {
        filename
      }
      ...BottlebooksStandardCatalog_Table
    }
  }

  fragment BottlebooksStandardCatalog_Table on RegistrationCatalogSection {
    registration {
      registrationId
      name
      companyId
      stand {
        name
      }

      collection {
        eventId: collectionId
        name
        site {
          publicUrl
        }
        logo {
          url
          fixed(width: 100, height: 20) {
            src
          }
        }
      }

      profile {
        name
        street
        postalCode
        city
        country
        telephone
        email
        website
        shortDescription
        ...ProfileLogo
        mainImage {
          fixed(width: 500, height: 255, gravity: FACES) {
            src
          }
        }
        autoSizedLogo: logo {
          url
        }
      }

      registeredProducts {
        nodes {
          maximeKlassifikation: customFieldValue(key: "maximeKlassifikation") {
            ... on MultiSelectFieldValue {
              value
            }
          }
          fassprobe: customFieldValue(key: "fassprobe") {
            ... on YesNoFieldValue {
              value
            }
          }
          producer {
            isSeekingDistribution
            distributionContacts {
              contactId
              companyName
            }
          }
          pricing {
            currency
            priceType
            price
          }
          productId
          product {
            productId
            name
            alcohol

            regionHierarchy
            denomination
            ...Blend
            ...BottleImage
            ... on Wine {
              vineyard
              designation
              residualSugar
              totalAcidity
            }
          }
        }
      }
    }
  }
  ${Product.Blend.fragment}
  ${Product.BottleImage.fragment}
  ${Company.ProfileLogo.fragment}
`;

function afterQuery(data, rest) {
  if (rest.companyId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.companyId === rest.companyId
    );
  }
  if (rest.registrationId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.registrationId === rest.registrationId
    );
  }

  return data.sections;
}
